import { FC, useEffect, useState } from 'react';
import { useHistory, useParams, generatePath } from 'react-router-dom';
import styled from '@emotion/styled';
import useCart from '@app/shop/hooks/useCart';
import { Button } from '@app/shop/components/Button';
import { rem } from '@app/styles/theme/layout';
import { routes } from '@app/shop/';
import { pushEcommerceEvent } from '@app/shop/tracking';

import {
  Magento2_CartItemInput,
  Magento2_ProductStockStatus,
} from '@__generated__/globalTypes';
import {
  PricingPageShopProduct_magento2_products_items_Magento2_BundleProduct as BundleProduct,
  PricingPageShopProduct_magento2_products_items_Magento2_VirtualProduct as VirtualProduct,
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants_product as VariantProduct,
} from '../../hook/__generated__/PricingPageShopProduct';
import { convertToEuro } from '../../../../helpers';

const CART_ROUTE_NAME = 'cart';

export interface AddToCartButtonProps {
  product?: BundleProduct | VirtualProduct | VariantProduct | null;
  quantity: number;
  children: JSX.Element;
}

const AddToCartButton: FC<AddToCartButtonProps> = ({
  product,
  quantity,
  children,
}) => {
  const { addItems, addProductsResult } = useCart();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { language } = useParams<{
    language: string;
  }>();

  useEffect(() => {
    const result = addProductsResult?.data?.magento2?.addProductsToCart;
    const cartRoute = routes.find((route) => route.name === CART_ROUTE_NAME)
      ?.path;

    if (!addProductsResult?.loading) {
      setLoading(false);
    }

    if (result && cartRoute && !result.userErrors?.length) {
      const cartUrl = generatePath(cartRoute, {
        language,
      });
      history.push(cartUrl);
    }
  }, [addProductsResult, history, language]);

  const isDisabled =
    product?.stockStatus === Magento2_ProductStockStatus.OUT_OF_STOCK;

  const addToCart = () => {
    const cartItem: Magento2_CartItemInput = {
      sku: product?.sku as string,
      quantity,
    };
    setLoading(true);
    addItems([cartItem]);

    const finalPrice = product?.priceRange?.minimumPrice?.finalPrice;

    pushEcommerceEvent('Add to Cart', {
      currencyCode: finalPrice?.currency,
      add: {
        products: [
          {
            name: product?.name,
            id: product?.sku,
            price: convertToEuro(
              Number(finalPrice?.value) * quantity,
              finalPrice?.currency
            ),
            quantity,
            brand: '',
            category: '',
          },
        ],
      },
    });
  };

  return (
    <StyledButton
      data-testid="add-to-cart-button"
      isLoading={loading}
      onClick={loading || isDisabled ? () => {} : addToCart}
      type="primary"
      disabled={isDisabled}
    >
      {children}
    </StyledButton>
  );
};

const StyledButton = styled(Button)`
  font-size: ${rem(16)};
  &:disabled {
    cursor: not-allowed;
  }
`;

export default AddToCartButton;
